import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useEligibleLeavePolicies } from '@/hooks/employees/useEligibleLeavePolicies';
import type {
  AddEmployeeFormValues,
  LeaveAllocationState,
  LeavePolicyPreview,
} from '@/types/employee';
import { LeaveEligibilityPreview } from './LeaveEligibilityPreview';

interface LeavePreviewSectionProps {
  values: AddEmployeeFormValues;
  allocationState: LeaveAllocationState;
  setAllocationState: React.Dispatch<
    React.SetStateAction<LeaveAllocationState>
  >;
  requiresBranch: boolean;
}

/** The Add employee form's leave eligibility preview and allocations. */
export function LeavePreviewSection({
  values,
  allocationState,
  setAllocationState,
  requiresBranch,
}: LeavePreviewSectionProps) {
  const { t } = useTranslation(TranslationNS.Employee);
  const canPreviewLeaves =
    values.dateOfJoining &&
    values.leaveEligibleFrom &&
    (!requiresBranch || values.branchId);

  // Must use the branch being created in, not the globally selected one.
  const { data, isLoading } = useEligibleLeavePolicies(
    {
      departmentId: values.departmentId,
      designationId: values.designationId,
      dateOfJoining: values.dateOfJoining,
      leaveEligibleFrom: values.leaveEligibleFrom,
      branchId: values.branchId,
    },
    !!canPreviewLeaves
  );

  // Branch/department/designation changes swap which policies apply, so drop
  // allocations for leave types the employee is no longer eligible for.
  useEffect(() => {
    if (!data) return;

    const policies: LeavePolicyPreview[] = data.data ?? [];
    const eligibleLeaveTypeIds = new Set(
      policies.flatMap((policy) =>
        policy.leaveTypes.map((leaveType) => leaveType.id)
      )
    );

    setAllocationState((prev) => {
      const retained = Object.keys(prev).filter((leaveTypeId) =>
        eligibleLeaveTypeIds.has(leaveTypeId)
      );

      if (retained.length === Object.keys(prev).length) return prev;

      const next: LeaveAllocationState = {};
      for (const leaveTypeId of retained) {
        next[leaveTypeId] = prev[leaveTypeId];
      }

      return next;
    });
  }, [data, setAllocationState]);

  if (!canPreviewLeaves) return null;

  return (
    <>
      <h2 className="text-md font-semibold mt-2">
        {t('employee_page.section.leave_eligibility')}
      </h2>

      <div className="pt-4">
        <LeaveEligibilityPreview
          allocationState={allocationState}
          setAllocationState={setAllocationState}
          policies={data?.data ?? []}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
